import { z } from 'zod';

import { PaymentMethod } from '../enums/payments';
import { MAX_BIGINT_MINOR } from '../utils/money';
import { CivilDateDto } from './civilDate';

/**
 * D9 refunds — the request wire contract (spec §3.7.2/§3.7.3).
 *
 * Two shapes, discriminated by `mode`, because they mean different things:
 * `manual` is an ATTESTATION that money was already returned outside MFW
 * (so it carries the actual return date, the method and a reason, and the
 * server posts the refund immediately), while `online` only ASKS the
 * provider (so it carries no completion facts at all — a client must not be
 * able to forge them, which is why both branches are `.strict()`).
 *
 * Nothing here decides how much may be refunded: currency, eligibility and
 * the available amount per charge are derived server-side from the charge
 * rows themselves (spec §3.7.2), never from the request.
 */

/** How many charges one logical refund operation may touch. */
export const REFUND_MAX_ALLOCATIONS = 100;
export const REFUND_MAX_REASON_LENGTH = 1000;

/** A positive minor-unit amount: decimal digits only, no sign, no leading
 *  zero, no separator, no exponent. `'0'` is rejected on purpose — a zero
 *  refund child must never exist (cancelling without a refund is its own
 *  action). */
const POSITIVE_MINOR_STRING = /^[1-9][0-9]*$/;

export const RefundTargetDto = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('membership'),
      userMembershipId: z.string().uuid(),
      clientId: z.string().uuid(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('booking'),
      bookingId: z.string().uuid(),
    })
    .strict(),
]);
export type ApiRefundTargetDto = z.infer<typeof RefundTargetDto>;

/** One `(charge, amount)` pair. Minor units travel as strings (BIGINT), so a
 *  number input is rejected rather than coerced. */
export const RefundAllocationDto = z
  .object({
    paymentTransactionId: z.string().uuid(),
    amountMinor: z
      .string()
      .regex(POSITIVE_MINOR_STRING, 'VALIDATION__Positive')
      // In Zod 3 a failed `.regex()` marks the result DIRTY, not aborted —
      // this refinement still runs even for '1.5'/'1e3'/'12.50'. It must
      // therefore be TOTAL: re-check the pattern itself before calling
      // `BigInt(value)`, which throws a raw `SyntaxError` (not a `ZodError`)
      // on anything non-integer. Without the guard that `SyntaxError`
      // propagates out of `parse`/`safeParse` alike and becomes a 500
      // through `ZodPipe` instead of a validation error. The column is
      // BIGINT: a bigger number would only fail in Postgres.
      .refine(
        (value) =>
          !POSITIVE_MINOR_STRING.test(value) ||
          BigInt(value) <= MAX_BIGINT_MINOR,
        { message: 'VALIDATION__MaxValue' },
      ),
  })
  .strict();
export type ApiRefundAllocationDto = z.infer<typeof RefundAllocationDto>;

/** Allocations of one request: at least one, at most
 *  `REFUND_MAX_ALLOCATIONS`, and never two against the SAME charge — two
 *  rows for one charge would each reserve against the same remaining
 *  balance, and the `(requestId, chargeTransactionId)` unique index would
 *  reject them at insert time anyway. */
const RefundAllocationsDto = z
  .array(RefundAllocationDto)
  .min(1, 'VALIDATION__Required')
  .max(REFUND_MAX_ALLOCATIONS, 'VALIDATION__Max')
  .superRefine((allocations, ctx) => {
    const seen = new Set<string>();
    allocations.forEach((allocation, index) => {
      if (seen.has(allocation.paymentTransactionId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'VALIDATION__NotAllowed',
          path: [index, 'paymentTransactionId'],
        });
      }
      seen.add(allocation.paymentTransactionId);
    });
  });

const refundRequestBase = {
  /** the caller's own key for this logical operation — retrying with the
   *  same key returns the same outcome, reusing it with different inputs is
   *  refused (spec §3.7.2) */
  idempotencyKey: z.string().uuid(),
  allocations: RefundAllocationsDto,
};

export const CreateManualPaymentRefundDto = z
  .object({
    mode: z.literal('manual'),
    ...refundRequestBase,
    /** the civil day the money actually went back — not the entry time,
     *  which the server stamps itself */
    occurredOn: CivilDateDto,
    method: z.nativeEnum(PaymentMethod),
    reason: z.string().min(1).max(REFUND_MAX_REASON_LENGTH),
  })
  .strict();

export const CreateOnlinePaymentRefundDto = z
  .object({
    mode: z.literal('online'),
    ...refundRequestBase,
  })
  .strict();

export const CreatePaymentRefundDto = z.discriminatedUnion('mode', [
  CreateManualPaymentRefundDto,
  CreateOnlinePaymentRefundDto,
]);
export type ApiCreatePaymentRefundDto = z.infer<typeof CreatePaymentRefundDto>;
export type ApiCreateManualPaymentRefundDto = z.infer<
  typeof CreateManualPaymentRefundDto
>;
export type ApiCreateOnlinePaymentRefundDto = z.infer<
  typeof CreateOnlinePaymentRefundDto
>;
